import { ButtonPrimary } from 'components/Button'
import FilterRow from 'components/FilterRow'
import { DynamicSizedLoadingRow } from 'components/Loader/styled'
import Pagination, { SearchResultStats } from 'components/PaginationControls'
import { ResponsiveButtonPrimary } from 'components/PositionList/styled'
import { DataTransitionOverlay, Spinner, SpinnerWithText } from 'components/Spinner'
import { MouseoverTooltip } from 'components/Tooltip'
import { useConnection } from 'hooks/useConnection'
import { useKodiakFarms } from 'hooks/useKodiakFarm'
import { RefreshControl } from 'pages/Liquidity'
import { Header, NetworkIcon } from 'pages/Liquidity/Liquidity.styles'
import { memo, Suspense, useCallback, useEffect, useMemo } from 'react'
import { FaSort, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa'
import { useToggleWalletModal } from 'state/application/hooks'
import { BaseMemoryFilter } from 'state/filterTypes'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { useSearchTerm } from 'state/kodiakFarms/hooks'
import { setSortBy, toggleFilter, updateQueryParams } from 'state/kodiakFarms/reducer'
import { KodiakFarmOrderBy, OrderDirection } from 'state/kodiakFarms/types'
import styled, { css, useTheme } from 'styled-components'
import { ThemedText } from 'theme'
import { KodiakFarm } from 'types/farms'

import FarmListItem from './FarmListItem'
import * as S from './styled'

const ITEMS_PER_PAGE = 20

type FarmsListProps = React.PropsWithChildren<{
  searchTerm?: string
  sortBy?: string
  sortDirection?: string
  minimumTvl?: number
}>

export const ErrorContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: auto;
  padding: 24px 0;
  max-width: 300px;
`

const IconStyle = css`
  width: 40px;
  height: 40px;
  margin-bottom: 0.5rem;
`

const SortBothIcon = ({ active }: { active?: boolean }) => (
  <S.SortIconWrapper active={active}>
    <FaSort />
  </S.SortIconWrapper>
)

const SortAscIcon = () => (
  <S.SortIconWrapper active={true}>
    <FaSortAmountUp />
  </S.SortIconWrapper>
)

const SortDescIcon = () => (
  <S.SortIconWrapper active={true}>
    <FaSortAmountDown />
  </S.SortIconWrapper>
)

const farmsLoader = () => (
  <>
    <DynamicSizedLoadingRow
      height="30em"
      style={{ width: '100%' }}
      borderRadius="16px"
      borderTopLeftRadius="0px"
      borderTopRightRadius="0px"
      borderBottomLeftRadius="16px"
      borderBottomRightRadius="16px"
    >
      <div />
    </DynamicSizedLoadingRow>
  </>
)

const getSortIcon = (
  columnOrderBy: KodiakFarmOrderBy,
  currentOrderBy: KodiakFarmOrderBy,
  orderDirection: OrderDirection
) => {
  if (columnOrderBy !== currentOrderBy) {
    return <SortBothIcon active={false} />
  }

  return orderDirection === OrderDirection.Desc ? <SortDescIcon /> : <SortAscIcon />
}

const FarmsContent = memo(
  ({ farms, isInTransition }: { farms: KodiakFarm[]; isInTransition?: boolean; isPaging?: boolean }) => {
    const visibleFarms = useMemo(() => {
      if (!farms || !Array.isArray(farms)) {
        return []
      }
      return farms.filter(Boolean)
    }, [farms])

    if (isInTransition) {
      return (
        <tbody>
          <tr>
            <td colSpan={7} style={{ width: '100%' }}>
              {farmsLoader()}
            </td>
          </tr>
        </tbody>
      )
    }

    return (
      <tbody>
        {visibleFarms.map((farm: KodiakFarm, index: number) => (
          <FarmListItem farm={farm} row={index} key={farm.id} />
        ))}
      </tbody>
    )
  }
)
FarmsContent.displayName = 'FarmsContent'

const ListHeader = memo(
  ({
    theme,
    orderBy,
    orderDirection,
    handleSort,
    isInTransition,
  }: {
    theme: any
    orderBy: KodiakFarmOrderBy
    orderDirection: OrderDirection
    handleSort: (newOrderBy: KodiakFarmOrderBy) => void
    isInTransition?: boolean
  }) => {
    const onSortClick = useCallback(
      (newOrderBy: KodiakFarmOrderBy) => {
        if (!isInTransition) {
          handleSort(newOrderBy)
        }
      },
      [handleSort, isInTransition]
    )

    return (
      <thead>
        <tr>
          <S.Th width="25%">
            <MouseoverTooltip
              text={
                <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
                  Permissionless farms for kodiak vaults
                </ThemedText.DeprecatedBody>
              }
            >
              <ThemedText.DeprecatedBody
                fontSize="16px"
                fontWeight="700"
                lineHeight="24px"
                color={theme.textSecondary}
                className="title"
              >
                Farm
              </ThemedText.DeprecatedBody>
            </MouseoverTooltip>
          </S.Th>

          <S.Th
            width="20%"
            onClick={() => onSortClick(KodiakFarmOrderBy.Apr)}
            sortable
            active={orderBy === KodiakFarmOrderBy.Apr}
            role="columnheader"
            aria-sort={
              orderBy === KodiakFarmOrderBy.Apr
                ? orderDirection === OrderDirection.Desc
                  ? 'descending'
                  : 'ascending'
                : undefined
            }
          >
            <MouseoverTooltip
              text={
                <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
                  Average APR based on fees and farm emissions
                </ThemedText.DeprecatedBody>
              }
            >
              <ThemedText.DeprecatedBody
                fontSize="16px"
                fontWeight="700"
                lineHeight="24px"
                color={theme.textSecondary}
                className="title"
              >
                APR {getSortIcon(KodiakFarmOrderBy.Apr, orderBy, orderDirection)}
              </ThemedText.DeprecatedBody>
            </MouseoverTooltip>
          </S.Th>

          <S.Th
            width="15%"
            onClick={() => onSortClick(KodiakFarmOrderBy.FarmTVL)}
            sortable
            active={orderBy === KodiakFarmOrderBy.FarmTVL}
            role="columnheader"
            aria-sort={
              orderBy === KodiakFarmOrderBy.FarmTVL
                ? orderDirection === OrderDirection.Desc
                  ? 'descending'
                  : 'ascending'
                : undefined
            }
          >
            <MouseoverTooltip
              text={
                <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
                  Total dollar TVL in the farm
                </ThemedText.DeprecatedBody>
              }
            >
              <ThemedText.DeprecatedBody
                fontSize="16px"
                fontWeight="700"
                lineHeight="24px"
                color={theme.textSecondary}
                className="title"
              >
                Farm TVL {getSortIcon(KodiakFarmOrderBy.FarmTVL, orderBy, orderDirection)}
              </ThemedText.DeprecatedBody>
            </MouseoverTooltip>
          </S.Th>

          <S.Th
            width="15%"
            onClick={() => onSortClick(KodiakFarmOrderBy.PoolTVL)}
            sortable
            active={orderBy === KodiakFarmOrderBy.PoolTVL}
            role="columnheader"
            aria-sort={
              orderBy === KodiakFarmOrderBy.PoolTVL
                ? orderDirection === OrderDirection.Desc
                  ? 'descending'
                  : 'ascending'
                : undefined
            }
          >
            <MouseoverTooltip
              text={
                <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
                  Total dollar TVL in the pool
                </ThemedText.DeprecatedBody>
              }
            >
              <ThemedText.DeprecatedBody
                fontSize="16px"
                fontWeight="700"
                lineHeight="24px"
                color={theme.textSecondary}
                className="title"
              >
                Pool TVL {getSortIcon(KodiakFarmOrderBy.PoolTVL, orderBy, orderDirection)}
              </ThemedText.DeprecatedBody>
            </MouseoverTooltip>
          </S.Th>

          <S.Th width="15%" role="columnheader">
            <MouseoverTooltip
              text={
                <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
                  User deposits in the farm
                </ThemedText.DeprecatedBody>
              }
            >
              <ThemedText.DeprecatedBody
                fontSize="16px"
                fontWeight="700"
                lineHeight="24px"
                color={theme.textSecondary}
                className="title"
              >
                My Deposits
              </ThemedText.DeprecatedBody>
            </MouseoverTooltip>
          </S.Th>

          <S.Th width="20%" role="columnheader">
            <MouseoverTooltip
              text={
                <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
                  Only deployer & reward manager can configure farm
                </ThemedText.DeprecatedBody>
              }
            >
              <ThemedText.DeprecatedBody
                fontSize="16px"
                fontWeight="700"
                lineHeight="24px"
                color={theme.textSecondary}
                className="title"
              >
                Config
              </ThemedText.DeprecatedBody>
            </MouseoverTooltip>
          </S.Th>
        </tr>
      </thead>
    )
  }
)
ListHeader.displayName = 'ListHeader'

function FarmsList({ refreshControl }: { refreshControl?: RefreshControl }) {
  const theme = useTheme()
  const { account } = useConnection()
  const toggleWalletModal = useToggleWalletModal()
  const searchTerm = useSearchTerm()
  const showConnectAWallet = Boolean(!account)
  const dispatch = useAppDispatch()

  const { orderBy, orderDirection, filters } = useAppSelector((state) => state.kodiakFarms)

  useEffect(() => {
    if (!orderBy) {
      dispatch(
        setSortBy({
          orderBy: KodiakFarmOrderBy.FarmTVL,
          orderDirection: OrderDirection.Desc,
        })
      )
    }
  }, [dispatch, orderBy])

  const {
    data: farms,
    isLoading,
    transitionType,
    isFetching,
    isError,
    isDataTransition,
    error,
    refetch,
    pagination: { currentPage, goToPage, totalPages, limit },
  } = useKodiakFarms(filters, {
    orderBy,
    orderDirection,
  })

  useEffect(() => {
    if (refreshControl?.onRegister && refetch) {
      const asyncRefetch = async () => {
        refetch()
      }
      refreshControl.onRegister(asyncRefetch)
    }

    return () => {
      if (refreshControl?.onRegister) {
        refreshControl.onRegister(null)
      }
    }
  }, [refreshControl?.onRegister, refetch])

  const handlePageChange = useCallback(
    (page: number) => {
      if (isDataTransition) return
      goToPage(page - 1)
      window.scrollTo(0, 0)
    },
    [goToPage, isDataTransition]
  )

  const handleSort = useCallback(
    (newOrderBy: KodiakFarmOrderBy) => {
      if (isDataTransition) return

      const newDirection =
        orderBy === newOrderBy && orderDirection === OrderDirection.Desc ? OrderDirection.Asc : OrderDirection.Desc

      dispatch(
        setSortBy({
          orderBy: newOrderBy,
          orderDirection: newDirection,
        })
      )

      dispatch(
        updateQueryParams({
          orderBy: newOrderBy,
          orderDirection: newDirection,
          offset: 0,
          limit,
        })
      )
    },
    [isDataTransition, dispatch, orderBy, orderDirection, limit]
  )

  const handleFilterChange = useCallback(
    (filter: BaseMemoryFilter) => {
      if (!isDataTransition) {
        dispatch(toggleFilter(filter.displayName))
      }
    },
    [dispatch, isDataTransition]
  )

  const getTransitionLabel = () => {
    if (refreshControl?.isRefreshing) {
      return 'Refreshing farms...'
    }
    if (transitionType === 'sort') {
      if (orderBy === KodiakFarmOrderBy.Apr) return 'Sorting by APR...'
      if (orderBy === KodiakFarmOrderBy.PoolTVL) return 'Sorting by Pool TVL...'
      if (orderBy === KodiakFarmOrderBy.FarmTVL) return 'Sorting by Farm TVL...'
      return 'Sorting...'
    } else if (transitionType === 'filter') {
      return 'Applying filters...'
    } else if (transitionType === 'search') {
      return 'Searching farms...'
    }
    return 'Loading farms...'
  }

  const showTransitionOverlay = isDataTransition || refreshControl?.isRefreshing || (isLoading && !isError)
  const showError = isError && !showTransitionOverlay
  const showContent = !showTransitionOverlay && !showError

  return (
    <S.PageWrapper>
      <FilterRow filters={filters} onFilterChange={handleFilterChange} />
      <S.ListContentWrapper>
        <S.TableWrapper>
          {showTransitionOverlay && (
            <DataTransitionOverlay>
              <SpinnerWithText>
                <Spinner />
                <ThemedText.DeprecatedBody color="white">{getTransitionLabel()}</ThemedText.DeprecatedBody>
              </SpinnerWithText>
            </DataTransitionOverlay>
          )}
          <S.Table>
            {!showError ? (
              <ListHeader
                theme={theme}
                orderBy={orderBy}
                orderDirection={orderDirection}
                handleSort={handleSort}
                isInTransition={showTransitionOverlay}
              />
            ) : (
              ''
            )}

            {showError ? (
              <tbody>
                <tr>
                  <td colSpan={7}>
                    <Header style={{ border: 'none' }}>
                      <ErrorContainer>
                        <ThemedText.DeprecatedBody color={theme.deprecated_text3} textAlign="center">
                          <NetworkIcon />
                          <div style={{ marginTop: 8 }}>Something went wrong while loading top pools.</div>
                        </ThemedText.DeprecatedBody>

                        <ResponsiveButtonPrimary style={{ marginTop: '1rem' }} onClick={() => refetch()}>
                          Try again
                        </ResponsiveButtonPrimary>
                      </ErrorContainer>
                    </Header>
                  </td>
                </tr>
              </tbody>
            ) : showTransitionOverlay ? (
              <tbody>
                <tr>
                  <td colSpan={6} style={{ height: '400px', width: '100%' }}></td>
                </tr>
              </tbody>
            ) : (
              <>
                {farms && farms.length > 0 ? (
                  <Suspense
                    fallback={
                      <tbody>
                        <tr>
                          <td colSpan={6} style={{ height: '400px', width: '100%' }}></td>
                        </tr>
                      </tbody>
                    }
                  >
                    <FarmsContent farms={farms} isInTransition={false} />
                  </Suspense>
                ) : (
                  <tbody>
                    <tr>
                      <td colSpan={6}>
                        <S.ErrorContainer>
                          <ThemedText.DeprecatedBody color={theme.deprecated_text3} textAlign="center">
                            <S.InboxIcon strokeWidth={1} />
                            <div>No farms found.</div>
                          </ThemedText.DeprecatedBody>
                          {showConnectAWallet && (
                            <ButtonPrimary
                              style={{ marginTop: '2em', padding: '8px 16px', width: '140px' }}
                              onClick={toggleWalletModal}
                            >
                              Connect wallet
                            </ButtonPrimary>
                          )}
                        </S.ErrorContainer>
                      </td>
                    </tr>
                  </tbody>
                )}
              </>
            )}
          </S.Table>

          {showContent && farms.length > 0 && (
            <Pagination currentPage={currentPage + 1} totalPages={totalPages} onPageChange={handlePageChange} />
          )}

          {searchTerm && showContent && farms.length > 0 && (
            <SearchResultStats>
              Showing {farms.length > 0 ? currentPage * ITEMS_PER_PAGE + 1 : 0}-
              {Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalPages)} of {totalPages} results
            </SearchResultStats>
          )}
        </S.TableWrapper>
      </S.ListContentWrapper>
    </S.PageWrapper>
  )
}

export default memo(FarmsList)
